import React, { useState, useEffect, useRef, useCallback } from "react";
import { TOKENS } from "Static/constant";
import { settingsStoreData } from "Stores/settingsData";
import TokenVariable from "Components/common/TokenVariable";

const T = {
  blue: "#3953FB",
  blueHover: "#2D44E0",
  blueFaint: "#EEF1FF",
  blueLight: "#C7CFFE",
  textPrimary: "#1F2937",
  textBody: "#374151",
  textSecondary: "#6B7280",
  textMuted: "#9CA3AF",
  white: "#FFFFFF",
  surfaceGray: "#F9FAFB",
  surfaceGray2: "#F3F4F6",
  border: "#E5E7EB",
  borderInput: "#D1D5DB",
  red: "#EF4444",
  redFaint: "#FEF2F2",
  ifBg: "#EEF1FF",
  ifText: "#3953FB",
  ifBorder: "#C7CFFE",
  elifBg: "#FFFBEB",
  elifText: "#D97706",
  elifBorder: "#FDE68A",
};

// Only these 5 operators are supported by the backend condition evaluator
const OPERATORS = [
  { value: "equals",     label: "equals" },
  { value: "not_equals", label: "does not equal" },
  { value: "contains",   label: "contains" },
  { value: "is_set",     label: "is set" },
  { value: "is_not_set", label: "is not set" },
];

const NEEDS_VALUE = ["equals", "not_equals", "contains"];
const SPINTAX_RE = /\{[^{}|]*\|[^{}]*\}/;

const mkCond = () => ({
  id: Date.now() + Math.random(),
  field: "",
  operator: "equals",
  value: "",
  content: "",
});

const Icon = ({ n, s = 16, c = "currentColor" }) => {
  const icons = {
    close: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    ),
    plus: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    ),
    trash: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
      </svg>
    ),
    chevron: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    ),
    chevronLeft: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
      </svg>
    ),
    info: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    ),
    check: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
    lightning: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
  };
  return icons[n] || null;
};

function useOutsideClick(ref, fn) {
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) fn();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, fn]);
}

function FieldSelect({ value, onChange, fields }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef();
  useOutsideClick(ref, useCallback(() => { setOpen(false); setSearch(""); }, []));

  const filtered = fields.filter(f =>
    f.text.toLowerCase().includes(search.toLowerCase())
  );
  const selected = fields.find(f => f.key === value);

  return (
    <div ref={ref} style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <button
        onClick={() => setOpen(p => !p)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", background: T.white, border: "1px solid " + (open ? T.blue : T.borderInput), borderRadius: 6, cursor: "pointer", fontSize: 13, color: value ? T.textBody : T.textMuted, fontFamily: "inherit", boxShadow: open ? `0 0 0 3px ${T.blueFaint}` : "none", transition: "all 0.15s" }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, textAlign: "left" }}>
          {selected ? selected.text : "Select field"}
        </span>
        <span style={{ transform: open ? "rotate(180deg)" : "none", transition: "0.15s", flexShrink: 0, marginLeft: 6 }}>
          <Icon n="chevron" s={13} c={T.textMuted} />
        </span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, width: "230px", background: T.white, borderRadius: 8, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.07), 0 10px 24px rgba(0,0,0,0.1)", border: "1px solid " + T.border, zIndex: 9999 }}>
          <div style={{ padding: "8px 9px", borderBottom: "1px solid " + T.border }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search fields…"
              style={{ width: "100%", border: "1px solid " + T.borderInput, borderRadius: 5, padding: "5px 9px", fontSize: 12.5, outline: "none", fontFamily: "inherit", boxSizing: "border-box", background: T.surfaceGray }}
            />
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {filtered.map(f => (
              <button
                key={f.key}
                onClick={() => { onChange(f.key); setOpen(false); setSearch(""); }}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", textAlign: "left", padding: "7px 12px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: T.textBody, fontFamily: "inherit" }}
                onMouseEnter={e => e.currentTarget.style.background = T.blueFaint}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                {f.text}
                {value === f.key && <Icon n="check" s={13} c={T.blue} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OperatorSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useOutsideClick(ref, useCallback(() => setOpen(false), []));
  const selected = OPERATORS.find(o => o.value === value);

  return (
    <div ref={ref} style={{ position: "relative", width: 168, flexShrink: 0 }}>
      <button
        onClick={() => setOpen(p => !p)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", background: T.white, border: "1px solid " + (open ? T.blue : T.borderInput), borderRadius: 6, cursor: "pointer", fontSize: 13, color: value ? T.textBody : T.textMuted, fontFamily: "inherit", boxShadow: open ? `0 0 0 3px ${T.blueFaint}` : "none", transition: "all 0.15s" }}
      >
        <span>{selected?.label || "operator"}</span>
        <span style={{ transform: open ? "rotate(180deg)" : "none", transition: "0.15s" }}>
          <Icon n="chevron" s={13} c={T.textMuted} />
        </span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, width: "100%", background: T.white, borderRadius: 8, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.07), 0 10px 24px rgba(0,0,0,0.1)", border: "1px solid " + T.border, zIndex: 9999, overflow: "hidden" }}>
          {OPERATORS.map(op => (
            <button
              key={op.value}
              onClick={() => { onChange(op.value); setOpen(false); }}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: T.textBody, fontFamily: "inherit" }}
              onMouseEnter={e => e.currentTarget.style.background = T.blueFaint}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              {op.label}
              {value === op.value && <Icon n="check" s={13} c={T.blue} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function ConditionBranch({ cond, index, total, onChange, onRemove, fields }) {
  const isIf = index === 0;
  const needsVal = NEEDS_VALUE.includes(cond.operator);
  const [showTokenPopup, setShowTokenPopup] = useState(false);
  const cursorPos = useRef(0);

  const handleTokenSelect = (value = "") => {
    window && window.getSelection() && window.getSelection().deleteFromDocument();
    const pos = cursorPos.current;
    const current = cond.content || "";
    const updated = current.slice(0, pos) + value + current.slice(pos);
    onChange({ ...cond, content: updated });
    cursorPos.current = pos + value.length;
    setShowTokenPopup(false);
  };

  return (
    <div style={{ background: T.white, border: "1px solid " + T.border, borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: T.surfaceGray, borderBottom: "1px solid " + T.border, borderRadius: "7px 7px 0 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 4px", cursor: "grab" }}>
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div key={i} style={{ width: 2.5, height: 2.5, borderRadius: "50%", background: T.textMuted }} />
            ))}
          </div>
          <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", background: isIf ? T.ifBg : T.elifBg, color: isIf ? T.ifText : T.elifText, border: `1px solid ${isIf ? T.ifBorder : T.elifBorder}`, borderRadius: 4, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em" }}>
            {isIf ? "IF" : "ELSE IF"}
          </span>
          <span style={{ fontSize: 12.5, color: T.textSecondary }}>Condition {index + 1}</span>
        </div>
        {total > 1 && (
          <button
            onClick={onRemove}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "3px 5px", borderRadius: 5, color: T.textMuted, display: "flex" }}
            onMouseEnter={e => { e.currentTarget.style.color = T.red; e.currentTarget.style.background = T.redFaint; }}
            onMouseLeave={e => { e.currentTarget.style.color = T.textMuted; e.currentTarget.style.background = "transparent"; }}
          >
            <Icon n="trash" s={14} />
          </button>
        )}
      </div>

      <div style={{ padding: "12px 12px 0" }}>
        <div style={{ display: "flex", gap: 7, alignItems: "flex-start", flexWrap: "wrap" }}>
          <FieldSelect value={cond.field} onChange={v => onChange({ ...cond, field: v })} fields={fields} />
          <OperatorSelect value={cond.operator} onChange={v => onChange({ ...cond, operator: v, value: "" })} />
          {needsVal && (
            <input
              value={cond.value}
              onChange={e => onChange({ ...cond, value: e.target.value })}
              placeholder="Enter value…"
              style={{ flex: 1, minWidth: 100, padding: "7px 10px", border: "1px solid " + T.borderInput, borderRadius: 6, fontSize: 13, color: T.textBody, fontFamily: "inherit", outline: "none", background: T.white, transition: "all 0.15s" }}
              onFocus={e => { e.target.style.borderColor = T.blue; e.target.style.boxShadow = `0 0 0 3px ${T.blueFaint}`; }}
              onBlur={e => { e.target.style.borderColor = T.borderInput; e.target.style.boxShadow = "none"; }}
            />
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px 4px 18px" }}>
        <div style={{ width: 1, height: 14, background: T.border }} />
        <span style={{ fontSize: 12, color: T.textMuted }}>then send</span>
      </div>

      <div style={{ padding: "0 12px 12px" }}>
        <textarea
          value={cond.content || ""}
          onChange={e => onChange({ ...cond, content: e.target.value })}
          onMouseUp={e => { cursorPos.current = e.target.selectionStart; }}
          onKeyUp={e => { cursorPos.current = e.target.selectionStart; }}
          placeholder="Define content when condition is matched"
          rows={4}
          style={{ width: "100%", padding: "8px 10px", border: "1px solid " + T.borderInput, borderRadius: 6, fontSize: 13, color: T.textBody, fontFamily: "inherit", outline: "none", background: T.white, resize: "vertical", boxSizing: "border-box", transition: "border-color 0.15s, box-shadow 0.15s" }}
          onFocus={e => { e.target.style.borderColor = T.blue; e.target.style.boxShadow = `0 0 0 3px ${T.blueFaint}`; }}
          onBlur={e => { e.target.style.borderColor = T.borderInput; e.target.style.boxShadow = "none"; }}
        />
        <div style={{ position: "relative", marginTop: 6 }}>
          <span
            onClick={() => setShowTokenPopup(p => !p)}
            style={{ fontSize: 12.5, color: T.blue, cursor: "pointer", userSelect: "none" }}
          >
            {`{ X } Insert Variable`}
          </span>
          {showTokenPopup && (
            <TokenVariable
              tokenCbk={handleTokenSelect}
              closeCbk={() => setShowTokenPopup(false)}
              pageType="sequences"
              showInboxTokens={false}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewLogic({ conditions, fallback }) {
  const labels = ["A", "B", "C", "D", "E"];
  const active = conditions.filter(c => c.field);

  if (!active.length && !fallback) {
    return (
      <div style={{ padding: "48px 24px", textAlign: "center" }}>
        <div style={{ opacity: 0.3, marginBottom: 10 }}><Icon n="info" s={28} c={T.textSecondary} /></div>
        <div style={{ fontSize: 14, fontWeight: 500, color: T.textSecondary, marginBottom: 4 }}>No conditions configured</div>
        <div style={{ fontSize: 13, color: T.textMuted }}>Switch to Configure to build your conditions</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 580, margin: "0 auto", padding: "28px 24px" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14 }}>Logic Flow</div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {active.map((c, i) => {
          const op = OPERATORS.find(o => o.value === c.operator);
          return (
            <div key={i}>
              <div style={{ display: "flex", borderRadius: 7, overflow: "hidden", border: "1px solid " + (i === 0 ? T.ifBorder : T.elifBorder) }}>
                <div style={{ padding: "9px 12px", background: i === 0 ? T.ifBg : T.elifBg, display: "flex", alignItems: "center", minWidth: 68, justifyContent: "center" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: i === 0 ? T.ifText : T.elifText }}>{i === 0 ? "IF" : "ELSE IF"}</span>
                </div>
                <div style={{ padding: "9px 12px", flex: 1, display: "flex", alignItems: "center", gap: 6, background: T.white, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600, color: T.blue, fontSize: 13 }}>{c.field}</span>
                  <span style={{ color: T.textSecondary, fontSize: 12.5 }}>{op?.label}</span>
                  {c.value && <span style={{ fontWeight: 500, color: T.textBody, background: T.surfaceGray2, padding: "1px 7px", borderRadius: 4, fontSize: 12.5 }}>"{c.value}"</span>}
                </div>
                <div style={{ padding: "9px 12px", display: "flex", alignItems: "center", gap: 5, background: T.white, borderLeft: "1px solid " + T.border }}>
                  <span style={{ fontSize: 12, color: T.textMuted }}>→</span>
                  <span style={{ background: T.blue, color: T.white, padding: "2px 8px", borderRadius: 4, fontSize: 11.5, fontWeight: 500 }}>Var. {labels[i]}</span>
                </div>
              </div>
              {(i < active.length - 1 || fallback) && <div style={{ marginLeft: 20, width: 1, height: 12, background: T.border }} />}
            </div>
          );
        })}
        {fallback && (
          <div style={{ display: "flex", borderRadius: 7, overflow: "hidden", border: "1px solid " + T.border }}>
            <div style={{ padding: "9px 12px", background: T.surfaceGray2, display: "flex", alignItems: "center", minWidth: 68, justifyContent: "center" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.textSecondary }}>ELSE</span>
            </div>
            <div style={{ padding: "9px 12px", flex: 1, display: "flex", alignItems: "center", background: T.white, fontSize: 13, color: T.textMuted }}>No conditions matched</div>
            <div style={{ padding: "9px 12px", display: "flex", alignItems: "center", gap: 5, background: T.white, borderLeft: "1px solid " + T.border }}>
              <span style={{ fontSize: 12, color: T.textMuted }}>→</span>
              <span style={{ background: T.textSecondary, color: T.white, padding: "2px 8px", borderRadius: 4, fontSize: 11.5, fontWeight: 500 }}>Fallback</span>
            </div>
          </div>
        )}
      </div>
      <div style={{ marginTop: 14, padding: "9px 12px", background: T.surfaceGray, borderRadius: 6, border: "1px solid " + T.border, fontSize: 12.5, color: T.textSecondary, display: "flex", gap: 6, alignItems: "flex-start" }}>
        <Icon n="info" s={13} c={T.textMuted} />
        Conditions are checked top-to-bottom. The first match wins — only one variation is sent per email.
      </div>
    </div>
  );
}

const stripHtml = (html) => {
  if (!html || !html.includes("<")) return html;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const ConditionBuilderPopup = ({ isOpen = false, onClose = () => {}, onSave = () => {}, initialConditions = [], initialFallback = "" }) => {
  const [conditions, setConditions] = useState([mkCond()]);
  const [fallback, setFallback] = useState("");
  const [tab, setTab] = useState("configure");
  const [showFallbackTokenPopup, setShowFallbackTokenPopup] = useState(false);
  const fallbackCursorPos = useRef(0);
  const fallbackInitial = useRef("");

  const customFields = settingsStoreData.customFields || {};
  const allFields = TOKENS.map(t => {
    if (t.key && t.key.includes("customField")) {
      return { key: t.key, text: customFields[t.key] || t.text || t.key, value: t.value };
    }
    return { key: t.key, text: t.text || t.key, value: t.value };
  }).filter(f => f.key);

  useEffect(() => {
    if (isOpen) {
      if (initialConditions && initialConditions.length > 0) {
        const conds = initialConditions.filter(c => !c.default);
        const fallbackCond = initialConditions.find(c => c.default);
        setConditions(
          conds.length > 0
            ? conds.map((c, i) => ({ ...c, id: c.id || Date.now() + i, content: stripHtml(c.content || "") }))
            : [mkCond()]
        );
        const fb = stripHtml(fallbackCond?.content || "") || stripHtml(initialFallback || "");
        setFallback(fb);
        fallbackInitial.current = fb;
      } else {
        setConditions([mkCond()]);
        setFallback("");
        fallbackInitial.current = "";
      }
      setTab("configure");
    }
  }, [isOpen, initialFallback]);

  const canSave = conditions.some(c => c.field && c.operator);

  const handleSave = () => {
    if (conditions.length > 10) {
      alert("Maximum 10 conditions allowed per block.");
      return;
    }
    // Validate spintax is not present in any branch content
    const allContents = [...conditions.map(c => c.content || ""), fallback || ""];
    for (const c of allContents) {
      if (SPINTAX_RE.test(c)) {
        alert("Spintax {option|option} is not allowed inside conditional blocks.");
        return;
      }
    }
    // Pass branches with camelCase field key matching backend contactFieldMap keys
    onSave({ branches: conditions, fallback });
    onClose();
  };

  return (
    <div style={{
      position: "absolute",
      inset: 0,
      background: T.surfaceGray,
      borderRadius: "inherit",
      transform: isOpen ? "translateX(0)" : "translateX(100%)",
      transition: "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)",
      zIndex: 1100,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", background: T.white, borderBottom: "1px solid " + T.border, flexShrink: 0, minHeight: 52 }}>
        <button
          onClick={onClose}
          style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", fontSize: 13, color: T.blue, fontFamily: "inherit", padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.75"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
        >
          <Icon n="chevronLeft" s={14} c={T.blue} />
          Back to email
        </button>

        <div style={{ display: "flex", border: "1px solid " + T.borderInput, borderRadius: 6, overflow: "hidden" }}>
          {[["configure", "Configure"], ["preview", "Preview Logic"]].map(([t, l], i) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{ padding: "6px 14px", border: "none", borderLeft: i > 0 ? "1px solid " + T.borderInput : "none", cursor: "pointer", fontSize: 12.5, fontFamily: "inherit", background: tab === t ? T.blueFaint : T.white, color: tab === t ? T.blue : T.textSecondary, transition: "all 0.15s" }}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {tab === "configure" ? (
          <div style={{ maxWidth: 580, margin: "0 auto", padding: "24px" }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px 5px 9px", background: T.blue, borderRadius: 6, marginBottom: 10 }}>
                <Icon n="lightning" s={13} c="#fff" />
                <span style={{ fontSize: 13, fontWeight: 500, color: "#fff", letterSpacing: "0.01em" }}>Add Condition</span>
              </div>
              <p style={{ margin: 0, fontSize: 13, color: T.textSecondary, lineHeight: 1.6 }}>
                Show different words or phrases based on conditions for better personalization. You can add up to 10 conditions and a fallback content if none of the conditions is satisfied.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {conditions.map((c, i) => (
                <ConditionBranch
                  key={c.id}
                  cond={c}
                  index={i}
                  total={conditions.length}
                  fields={allFields}
                  onChange={v => setConditions(p => p.map((x, j) => j === i ? v : x))}
                  onRemove={() => setConditions(p => p.filter((_, j) => j !== i))}
                />
              ))}
            </div>

            {conditions.length < 10 && (
              <button
                onClick={() => setConditions(p => [...p, mkCond()])}
                style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 14px", background: T.white, border: "1px dashed " + T.borderInput, borderRadius: 6, cursor: "pointer", fontSize: 13, color: T.textSecondary, fontFamily: "inherit", width: "100%", justifyContent: "center", transition: "all 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.background = T.blueFaint; e.currentTarget.style.borderColor = T.blue; e.currentTarget.style.color = T.blue; }}
                onMouseLeave={e => { e.currentTarget.style.background = T.white; e.currentTarget.style.borderColor = T.borderInput; e.currentTarget.style.color = T.textSecondary; }}
              >
                <Icon n="plus" s={14} c="currentColor" />
                Add another condition
                <span style={{ fontSize: 12, color: T.textMuted }}>({conditions.length}/10)</span>
              </button>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 12px" }}>
              <div style={{ flex: 1, height: 1, background: T.border }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", whiteSpace: "nowrap" }}>Otherwise (Fallback)</span>
              <div style={{ flex: 1, height: 1, background: T.border }} />
            </div>

            <div style={{ background: T.white, border: "1px solid " + T.border, borderRadius: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: T.surfaceGray, borderBottom: "1px solid " + T.border, borderRadius: "7px 7px 0 0" }}>
                <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", background: T.surfaceGray2, color: T.textSecondary, borderRadius: 4, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", border: "1px solid " + T.borderInput }}>ELSE</span>
                <span style={{ fontSize: 12.5, color: T.textSecondary }}>Sent when no conditions match</span>
              </div>
              <div style={{ padding: "12px" }}>
                <textarea
                  value={fallback}
                  onChange={e => setFallback(e.target.value)}
                  onMouseUp={e => { fallbackCursorPos.current = e.target.selectionStart; }}
                  onKeyUp={e => { fallbackCursorPos.current = e.target.selectionStart; }}
                  placeholder="Write fallback content shown when none of the conditions match…"
                  rows={4}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid " + T.borderInput, borderRadius: 6, fontSize: 13, color: T.textBody, fontFamily: "inherit", outline: "none", background: T.white, resize: "vertical", boxSizing: "border-box", transition: "border-color 0.15s, box-shadow 0.15s" }}
                  onFocus={e => { e.target.style.borderColor = T.blue; e.target.style.boxShadow = `0 0 0 3px ${T.blueFaint}`; }}
                  onBlur={e => { e.target.style.borderColor = T.borderInput; e.target.style.boxShadow = "none"; }}
                />
                <div style={{ position: "relative", marginTop: 6 }}>
                  <span
                    onClick={() => setShowFallbackTokenPopup(p => !p)}
                    style={{ fontSize: 12.5, color: T.blue, cursor: "pointer", userSelect: "none" }}
                  >
                    {`{ X } Insert Variable`}
                  </span>
                  {showFallbackTokenPopup && (
                    <TokenVariable
                      tokenCbk={(value = "") => {
                        window && window.getSelection() && window.getSelection().deleteFromDocument();
                        const pos = fallbackCursorPos.current;
                        setFallback(prev => prev.slice(0, pos) + value + prev.slice(pos));
                        fallbackCursorPos.current = pos + value.length;
                        setShowFallbackTokenPopup(false);
                      }}
                      closeCbk={() => setShowFallbackTokenPopup(false)}
                      pageType="sequences"
                      showInboxTokens={false}
                    />
                  )}
                </div>
              </div>
            </div>

            <div style={{ height: 20 }} />
          </div>
        ) : (
          <PreviewLogic conditions={conditions} fallback={fallback} />
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: "12px 20px", borderTop: "1px solid " + T.border, background: T.white, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12.5, color: T.textMuted }}>
          <Icon n="info" s={13} c={T.textMuted} />
          Conditions checked top-to-bottom — first match wins
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={onClose}
            style={{ padding: "7px 16px", background: T.white, border: "1px solid " + T.blue, borderRadius: 6, cursor: "pointer", fontSize: 13.5, color: T.blue, fontFamily: "inherit", transition: "background 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.background = T.blueFaint}
            onMouseLeave={e => e.currentTarget.style.background = T.white}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{ padding: "7px 22px", background: canSave ? T.blue : T.surfaceGray2, border: "none", borderRadius: 6, cursor: canSave ? "pointer" : "not-allowed", fontSize: 13.5, color: canSave ? T.white : T.textMuted, fontFamily: "inherit", transition: "all 0.15s" }}
            onMouseEnter={e => { if (canSave) e.currentTarget.style.background = T.blueHover; }}
            onMouseLeave={e => { if (canSave) e.currentTarget.style.background = T.blue; }}
          >
            Save Condition
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConditionBuilderPopup;
